import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { StatusBadge } from '../components/Layout'
import { InvoiceEditor } from '../components/InvoiceEditor'
import { Btn, Msg, Stat, inputCls, money, fmtDate, today } from '../components/ui'
import { INVOICE_STATUSES, clientLabel, type Client, type Invoice } from '../lib/db'

/** Onglet Factures de la page Comptabilité (paramètres d'URL : new=1, client=, project=, edit=<id>). */
export default function FacturesPanel() {
  const [params, setParams] = useSearchParams()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [status, setStatus] = useState('tous')
  const [editor, setEditor] = useState<null | { data?: Invoice; clientId?: string; projectId?: string }>(null)
  const [msg, setMsg] = useState('')

  async function load() {
    const [i, c] = await Promise.all([supabase.from('invoices').select('*').order('created_at', { ascending: false }), supabase.from('clients').select('*').order('company').order('full_name')])
    const list = (i.data as any) || []; setInvoices(list); setClients((c.data as any) || [])
    const edit = params.get('edit'); if (edit) setEditor({ data: list.find((x: Invoice) => x.id === edit) })
    if (params.get('new')) setEditor({ clientId: params.get('client') || undefined, projectId: params.get('project') || undefined })
  }
  useEffect(() => { load() }, [])
  const cname = (id: string) => clientLabel(clients.find(c => c.id === id))
  const close = () => { setEditor(null); setParams({ tab: 'factures' }); load() }
  const isLate = (i: Invoice) => ['envoyée', 'en retard'].includes(i.status) && !!i.due_date && i.due_date < today()

  async function markPaid(i: Invoice) {
    const { error } = await supabase.from('invoices').update({ status: 'payée', paid_date: today() }).eq('id', i.id)
    if (error) setMsg('Erreur : ' + error.message); else load()
  }
  async function remove(i: Invoice) {
    if (!confirm(`Supprimer la facture ${i.invoice_number || ''} ?`)) return
    await supabase.from('invoice_items').delete().eq('invoice_id', i.id)
    const { error } = await supabase.from('invoices').delete().eq('id', i.id)
    if (error) setMsg('Erreur : ' + error.message); else load()
  }

  const filtered = invoices.filter(i => status === 'tous' || (status === 'en retard' ? isLate(i) : i.status === status))
  const sum = (f: (i: Invoice) => boolean) => money(invoices.filter(f).reduce((s, i) => s + Number(i.amount), 0))
  const year = new Date().getFullYear().toString()

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4"><h2 className="font-display text-2xl tracking-wide">Factures</h2><Btn onClick={() => setEditor({})}>+ Nouvelle facture</Btn></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="À encaisser" value={sum(i => ['envoyée', 'en retard'].includes(i.status))} />
        <Stat label="En retard" value={sum(isLate)} sub={`${invoices.filter(isLate).length} facture(s)`} />
        <Stat label={`Encaissé ${year}`} value={sum(i => i.status === 'payée' && (i.paid_date || i.created_at).startsWith(year))} />
        <Stat label="Brouillons" value={invoices.filter(i => i.status === 'brouillon').length} />
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        <select className={inputCls + ' w-auto'} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="tous">Toutes</option>{INVOICE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <Msg msg={msg} />
      <div className="border border-white/10 rounded overflow-x-auto mt-2">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-white/5 text-acier text-[11px] uppercase tracking-widest">
            <tr><th className="text-left px-4 py-3">Numéro</th><th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Émise</th><th className="text-left px-4 py-3">Échéance</th><th className="text-right px-4 py-3">Total</th><th className="text-left px-4 py-3">Statut</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {filtered.map(i => (
              <tr key={i.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3"><button onClick={() => setEditor({ data: i })} className="font-medium hover:text-orange">{i.invoice_number || 'Facture'}</button></td>
                <td className="px-4 py-3"><a href={`/admin/clients/${i.client_id}`} className="text-acier hover:text-orange">{cname(i.client_id)}</a></td>
                <td className="px-4 py-3 text-acier">{fmtDate(i.created_at)}</td>
                <td className={`px-4 py-3 ${isLate(i) ? 'text-red-300' : 'text-acier'}`}>{fmtDate(i.due_date)}</td>
                <td className="px-4 py-3 text-right">{money(i.amount)}</td>
                <td className="px-4 py-3"><StatusBadge status={isLate(i) ? 'en retard' : i.status} /></td>
                <td className="px-4 py-3 text-right whitespace-nowrap text-xs uppercase tracking-widest">
                  <a href={`/factures/${i.id}`} target="_blank" rel="noreferrer" className="text-acier hover:text-orange mr-3">PDF</a>
                  {i.status !== 'payée' && i.status !== 'annulée' && <button onClick={() => markPaid(i)} className="text-green-300 hover:underline mr-3">Payée</button>}
                  <button onClick={() => remove(i)} className="text-acier hover:text-red-300">Suppr.</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-acier">Aucune facture.</td></tr>}
          </tbody>
        </table>
      </div>
      {editor && <InvoiceEditor initial={editor.data} clients={clients} presetClientId={editor.clientId} presetProjectId={editor.projectId} onClose={close} onSaved={() => {}} />}
    </div>
  )
}
